export type PossessiveLanguage = 'da' | 'en';

/** Danish adds only an apostrophe when the name already ends in s, x, or z. */
const DANISH_APOSTROPHE_ONLY = new Set(['s', 'x', 'z']);

/**
 * Possessive form of a person's name.
 *
 * Danish: append s (Laura → Lauras). Names ending in s, x, or z take only
 * an apostrophe (Jens', Max', Inez').
 * English: always append 's, matching the existing UI (Jens's, Laura's).
 *
 * Blank names and placeholders with no letters (for example "—") fall back
 * to "patientens" / "the patient's". A custom fallback is inserted as-is.
 */
export function possessiveName(
  name: string | null | undefined,
  language: PossessiveLanguage,
  fallback?: string,
): string {
  const trimmed = name?.trim() ?? '';
  if (!trimmed || !/\p{L}/u.test(trimmed)) {
    return missingNameFallback(language, fallback);
  }

  switch (language) {
    case 'da':
      return DANISH_APOSTROPHE_ONLY.has(lastCharacter(trimmed)) ? `${trimmed}'` : `${trimmed}s`;
    case 'en':
      return `${trimmed}'s`;
    default: {
      const _exhaustive: never = language;
      throw new Error(`Unhandled possessive language: ${_exhaustive}`);
    }
  }
}

function missingNameFallback(language: PossessiveLanguage, fallback?: string): string {
  const custom = fallback?.trim() ?? '';
  if (custom) return custom;

  switch (language) {
    case 'da':
      return 'patientens';
    case 'en':
      return "the patient's";
    default: {
      const _exhaustive: never = language;
      throw new Error(`Unhandled possessive language: ${_exhaustive}`);
    }
  }
}

function lastCharacter(value: string): string {
  const chars = Array.from(value);
  return (chars[chars.length - 1] ?? '').toLowerCase();
}
